/* ============================================
   QUOTE DASHBOARD -- Shipping/Tracking detail card
   Asset: dashboard-shipping-tracking-card.js

   Extracted from dashboard-main.js buildDetailHtml on 2026-05-23 as
   Phase C.1 of the dashboard refactor. After Phase A + Phase B (9 ships)
   + B.3.b SURGICAL (buildQuoteRow) shipped today, this is the next-
   biggest tightly-coupled block in buildDetailHtml -- ~484 LOC of
   admin-side per-vendor tracking + per-bucket shipping + dims gate
   + carrier autocomplete machinery, all gated on
   `_effectiveAdmin && q.lifecycleStatus === \'sold\' && !q.linkedParentRef`.

   What this module does (admin-side card only):
     - Renders the per-vendor tracking row for each vendor on the order
       (carrier autocomplete + tracking type dropdown + tracking # +
       optional URL + Mark Fulfilled / Notify-via-Shopify button).
     - Switches to per-bucket rows when the order is a single-vendor
       mixed-cart online order with 2+ shippingBuckets (SHOP-1681-style
       broker-freight + supplier-parcel split). Each bucket gets its own
       row + per-bucket Mark Fulfilled (Shopify partial fulfillment).
     - Flips card title between "Tracking" and "Fulfillment" based on
       whether dims have been logged on this order AND whether the upper
       bucket-aware Fulfillment card already rendered.
     - Shows the inline per-vendor "Log dims" link / pallet chip on offline-
       sold rows (saved state -> chip + Edit, unsaved -> Log dims link).
     - Locks tracking inputs when PO hasn\'t been sent yet (gated on
       isPoSentForVendor for vendor mode, bucket\'s own PO for bucket mode).
     - Direct-purchased vendors (purchasedDirectly) bypass the PO-sent
       lock -- admin can enter tracking even though the vendor never
       had a real PO.

   The card does NOT include:
     - The early bucket-aware Fulfillment card render (stays in main --
       calls existing renderFreightBalanceCard from dashboard-shipping-
       cards.js).
     - The rep-view Summary card (different code path -- gated on
       !_effectiveAdmin instead of effectiveAdmin -- stays in main).
     - The post-card Freight Balance + deferred AR/AP renders (stay in
       main).

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API attached to window.RQD:

     buildShippingTrackingCard(q, opts) -> html string

       opts.effectiveAdmin: boolean -- gate; returns \'\' if false
       opts.renderedFulfillmentEarly: boolean -- affects title flip
         (when the upper Fulfillment card already rendered, this card
         keeps the title as "Tracking" to avoid two FULFILLMENT-titled
         cards stacked).

     Self-gates on q.lifecycleStatus === \'sold\', !q.linkedParentRef,
     and vendors.length > 0. Returns \'\' when any gate fails.

   Init contract (RQD.initShippingTrackingCard called once by main.js
   at boot):
     esc, formatDate, money,
     vendorNamesMatch, isPostShipInvoiceVendor, vendorIsShipped -- all
       function declarations in main, safe to pass by reference per the
       init-deps-var-hoisting-trap memo.
     carriers -- ONLINE_CARRIERS array passed by reference (module-level
       const in main, used by other handlers there so it stays in main).
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initShippingTrackingCard (from main.js boot)
  var esc, formatDate, money;
  var vendorNamesMatch, isPostShipInvoiceVendor, vendorIsShipped;
  var vendorEarlyStampVeto; // CUSTOM_EARLYSTAMP_BY_CLAUDE (AIOPS fb2ebf0cafb8)
  var sectionTitleHtml;
  var carriers;

  /* CUSTOM_PICKUPDAY_BY_CLAUDE (AIOPS 93a12a634ef3 + ONYX b484f58ab35d, Ryan 2026-09-25/26) -- WHICH
     RULE APPLIES, ON THE ROW. A booked pickup now means two customer emails: the pickup-day email on
     the booked day (carrier, PRO, dispatch will call) and the shipped notice the morning after, as a
     reply on it. The worker writes `customerShippingEmails[vendor].pickupDay` when the first goes;
     `heldForPickup` is the shipped notice parked behind the floor. Returns {text, title} or null.
     Pure over the row, so a test can run it. */
  function pickupDayCaption(cse, ord) {
    var pd = cse && cse.pickupDay;
    var held = cse && cse.heldForPickup;
    if (pd && pd.at) {
      return {
        text: '📦 Pickup-day email sent' + (held && held.releaseOn ? ' · shipped ' + formatDate(held.releaseOn) : ''),
        title: 'Abby emailed the customer on the pickup day (' + formatDate(pd.at) + '): carrier, PRO number and that dispatch will call them.'
          + (pd.indeterminate ? ' Outlook never confirmed the send; check Sent Items in orders@.' : '')
          + ' The shipped notice follows the morning after as a reply on that email, unless the pickup is confirmed sooner.',
      };
    }
    var trackable = !!(ord && ord.tracking && !/^\s*BOL/i.test(String(ord.trackingType || '')));
    if (held && ord && ord.pickupScheduledFor && !ord.pickupConfirmedAt && trackable) {
      return {
        text: '⌛ Pickup email ' + formatDate(ord.pickupScheduledFor) + ' · shipped ' + formatDate(held.releaseOn),
        title: 'Pickup booked for ' + formatDate(ord.pickupScheduledFor) + '. On that day Abby emails the customer that the carrier is scheduled to pick it up (with the PRO and the dispatch call-ahead). The shipped notice waits until the morning after (' + formatDate(held.releaseOn) + '), unless the pickup is confirmed sooner.',
      };
    }
    return null;
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) -- Expanded type list.
     Carriers use a grab bag of names for the customer-trackable
     identifier -- PRO# (LTL), BOL# (heavy freight), Shipment ID
     (FedEx Freight new portal), Reference # (XPO), Pickup #
     (some 3PLs), Order # (parcel-blend). \'Other\' is the
     free-text escape for anything not in the list. */
  var trackingTypes = ['Tracking #', 'PRO #', 'BOL #', 'Shipment #', 'Shipment ID', 'Reference #', 'Pickup #', 'PO #', 'Order #', 'Other'];

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (AIOPS 1c5409fdb018 + fc5b7ad0298d, Ryan 2026-09-02)
     VENDOR READY — the supplier's own ready-date, one row PER VENDOR.

     ⛔ PER VENDOR, NOT PER DEAL, and the first cut got that wrong. It stored one date on the
     lifecycle record and drew one row above the whole card, which is only unambiguous while a deal
     has a single supplier. On a two-vendor deal ECORE might be ready on the 18th and Legend on the
     25th; one field meant whichever was typed last, and read as fact. Ryan asked the question that
     caught it: "is this row set up to handle multi-vendor orders?"

     ⛔ IT IS THE VENDOR'S DOCK DATE, NOT A DELIVERY DATE — ECORE's "Scheduled Ship", the day they
     expect it ready to leave THEIR dock. Treat as ± a day; a "ready" notice after ~3pm Denver means
     the earliest pickup is the next business day.

     Built from the tracking row's own classes (.rqd-vendor-item / .rqd-vendors__check /
     .rqd-vendor-item__name / .rqd-vendor-row-controls) so weight, colour, the check circle and the
     control column come from the same rules rather than a second set that agrees only today. */
  function vendorEtaRowHtml(q, v, shipped) {
    var map = (q.vendorEta && typeof q.vendorEta === 'object') ? q.vendorEta : null;
    /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (Ryan 2026-09-26 - RW-1317-C) — THE ENTRY UNDER ANY NAME FOR THIS
       SUPPLIER. RW-1317-C quotes "ECORE" and carries its ready date (9/23) under "ECORE Athletic", so
       an exact lookup drew an empty row — which, with the n/a rule, then claimed no date was ever
       given. Exact key first, then vendorNamesMatch, the tracking row's own rule (trackingRowKey).
       ⛔ `etaKey` rides data-vendor below, so a save rewrites the entry that was SHOWN (saveVendorEta
       carries its stored note by that key); writing under `v` would have dropped the note. */
    var etaKey = v;
    if (map && !Object.prototype.hasOwnProperty.call(map, v)) {
      for (var _ek in map) {
        if (Object.prototype.hasOwnProperty.call(map, _ek) && vendorNamesMatch(v, _ek)) { etaKey = _ek; break; }
      }
    }
    var ve = (map && map[etaKey] && typeof map[etaKey] === 'object') ? map[etaKey] : null;
    var d = (ve && ve.date) || '';
    /* CUSTOM_READYBY_BY_CLAUDE (AIOPS adefb81882f0, Ryan 2026-09-11) - a supplier may answer with a
       WINDOW rather than a date (RW-1339-A: FerusPrime said "early October").
       ⛔ WHAT IS SHOWN IS WHAT THEY SAID. The worker stores the window's last day in `date` so
       everything that plans against one keeps working, but printing 2026-10-10 here would turn
       their hedge into our commitment in front of whoever reads the card next.
       ⚠️ `kind: unparsed` is words we could not turn into a window at all ("when the container
       lands"). Worth showing, and NOT an answer - no tick, because nothing downstream may promise
       a customer anything on it. */
    var isRange = !!(ve && ve.kind === 'range');
    var isUnparsed = !!(ve && ve.kind === 'unparsed');
    var answered = !!d;
    var said = (ve && ve.label) || '';
    /* CUSTOM_READYBY_BY_CLAUDE (AIOPS b641360cdb02, Ryan 2026-09-16) — ⛔ A RANGE WITH NO LABEL
       FELL THROUGH TO ITS LAST DAY. `shown` was `said || d`, so a window typed as two dates — the
       shape this row can now save — printed "2026-10-31" and read as a firm commitment. It is the
       same lie the board chip was telling, one screen in. The label still wins when the supplier
       gave us words; the window is what we say when they did not. */
    /* CUSTOM_READYBYUI_BY_CLAUDE (Ryan 2026-09-19) — `shown` and the `readyByRangeText` call that
       fed it are GONE with the span they were built for (see the row below). Nothing else on this
       card printed either one.
       ⚠️ `RQD.readyByRangeText` ITSELF STAYS — it is the board chip's formatter
       (dashboard-quote-row.js) and the one place "Oct 6–13" is worded. This row simply stopped
       being a second caller of it. */
    var h = '';
    /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (Ryan 2026-09-26) \u2014 "N/A" ONCE THE SHIPMENT IS OUT. Ryan: "let's say
       something ships and gets the checkmark next to it, but we never added a 'vendor ready' date b/c
       maybe we didn't have one ... should the checkbox for vendor ready fill and get the 'n/a' that we
       use elsewhere in the DD ... and still ghost out the fields/buttons since they're no longer
       relevant?" Yes. `shipped` is the shipment row's OWN \u2713 (trackingRowTicked: told or delivered),
       so the two rows of one supplier cannot disagree. The badge is the AR/AP Invoiced row's n/a:
       the same green fill as a \u2713 with a tiny lowercase label \u2014 a third state, not a done.
       \u26a0\ufe0f LOOK ONLY. The inputs stay typeable (the ghost is CSS on `--na`), because a late ready date
       is still a fact worth recording. A row WITH a date keeps its \u2713; its boxes ghost as filled. */
    var etaNa = !answered && !!shipped;
    /* `--answered` (Ryan, RW-1317-C PowerBlock): once the row HAS a date, every box ghosts together —
       an unused End box stayed solid green and read as still owed. Kept in step on save by
       saveVendorEta's caller in dashboard-main.js (the Start box's value). */
    h += '<div class="rqd-vendor-item rqd-vendor-eta-row' + (etaNa ? ' rqd-vendor-eta-row--na' : '') + (answered ? ' rqd-vendor-eta-row--answered' : '') + '" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(etaKey) + '"'
      + (etaNa ? ' title="Shipped without a vendor ready date, so it is no longer needed."' : '') + '>';
    /* Display-only, like the vendor row's own check: it reports whether a date is recorded, it is
       not something you tick. */
    h += '<div class="rqd-vendors__check' + ((answered || etaNa) ? ' rqd-vendors__check--done' : '') + (etaNa ? ' rqd-toggle__check--na' : '') + '" style="pointer-events:none;cursor:default;"><span style="font-size:' + (etaNa ? '8px' : '12px') + ';">' + (answered ? '\u2713' : (etaNa ? 'n/a' : '')) + '</span></div>';
    /* END CUSTOM_DDCARDCOUNT_BY_CLAUDE */
    h += '<div class="rqd-vendor-item__namegroup">';
    /* The guidance rides as a TOOLTIP on the label rather than a line under the row. As a printed
       hint it repeated once per vendor, wedging a paragraph between every Vendor-ready/vendor pair
       on a multi-supplier deal. The deal-specific half a rep actually needs -- "ready 3pm, pickup
       next day" -- is already visible in the note input beside it. */
    h += '<span class="rqd-vendor-item__name" title="The vendor’s own ready date: when they expect it ready to leave THEIR dock, not a delivery date. Treat as about ± a day. Ready after ~3pm Denver means the earliest pickup is the next business day.">Vendor ready</span>';
    /* The as-of stamp sits in the slot the vendor row uses for its ship date. A vendor date with
       no as-of cannot be aged, and a three-week-old one read as fresh is what fc5b7ad0298d was
       filed to prevent. */
    /* The window (or the unparsed words) reads beside the label; the as-of stamp keeps its slot.
       A firm date needs neither - it is already in the date input. */
    /* CUSTOM_READYBYUI_BY_CLAUDE (Ryan 2026-09-19) — ⛔ THE WINDOW IS NOT PRINTED TWICE.
       Ryan: *"you don't need to repeat any window text... the row reads 'Vendor ready   first week
       of December  - as of 9/14/2026'. Instead it could just read 'Vendor ready - updated
       9/14/2026'."* The words were shown here AND sitting in the window input three controls to
       the right, so the row said the same thing twice on one line.
       ⚠️ NOTHING IS LOST IN EITHER SHAPE, which is what makes this safe to delete rather than
       move: a supplier's WORDS live in the window input (`value="' + said + '"`), and a window
       typed as two DATES lives in the two date boxes. The span was the only copy of neither.
       ⛔ THE `unparsed` TINT GOES WITH IT AND THAT COSTS SOMETHING — words we could not read used
       to be marked `--vague` here. They still show (in the window box) and still get no tick,
       which is the part that matters: nothing downstream may promise a customer anything on an
       unparsed entry, and that is decided by `kind`, never by this styling. */
    /* ⛔ "updated", NOT "as of" (Ryan's own wording). Same field, same meaning — how current the
       supplier's answer is — and with the window text gone this is the only thing left on the
       line, so it has to read as plain English rather than as a qualifier on a phrase that is no
       longer beside it. The tooltip keeps the precise sense, because a stale ready-by read as
       fresh is what fc5b7ad0298d was filed to prevent. */
    if (ve && ve.asOf && (answered || isUnparsed)) {
      h += '<span class="rqd-toggle__date" title="' + esc('How current the supplier’s answer is'
        + (isUnparsed ? ' — and they gave no date we could read, so nothing will promise a customer anything on this.' : '.')
        + ' Not when it ships.') + '">updated ' + formatDate(ve.asOf) + '</span>';
    }
    /* CUSTOM_CUSTOMERDATE_BY_CLAUDE (ONYX 6481820ed294, Ryan 2026-10-09) - THE CUSTOMER DATE, BESIDE THE SUPPLIER'S. The worker
       puts `customerDate` on every firm entry (lib/customer-date.js: the next workday after the supplier's date, weekends and
       federal holidays skipped) and every customer email and agent uses it. A window has none: the customer hears the
       supplier's own words. Look only; nothing here is typed or saved. */
    if (ve && ve.kind === 'firm' && ve.customerDate && /^\d{4}-\d{2}-\d{2}$/.test(ve.customerDate)) {
      var _cdParts = ve.customerDate.split('-');
      var _cdDow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(Date.UTC(+_cdParts[0], +_cdParts[1] - 1, +_cdParts[2], 12)).getUTCDay()];
      h += '<span class="rqd-toggle__date rqd-vendor-eta-customer" title="' + esc('The date every customer email and agent uses: the next workday after the supplier’s ready date (weekends and federal holidays skipped).') + '">customer date ' + _cdDow + ' ' + (+_cdParts[1]) + '/' + (+_cdParts[2]) + '</span>';
    }
    h += '</div>';
    h += '<div class="rqd-vendor-row-controls">';
    h += '<span class="rqd-toggle__saved rqd-vendor-eta-saved"></span>';
    /* CUSTOM_READYBYUI_BY_CLAUDE (Ryan 2026-09-19) — ⛔ THE NOTE INPUT IS GONE, THE NOTE IS NOT.
       Ryan: "get rid of the note field, I don't think it's needed or useful." The INPUT is what
       was removed; `vendorEta[vendor].note` is still stored, still written by agents, and still
       carried through every save from this row — see saveVendorEta in dashboard-main.js, which now
       reads the stored value instead of an input that no longer exists.
       ⚠️ THAT CARRY-THROUGH IS LOAD-BEARING, NOT TIDINESS. `note` was read as
       `(noteEl && noteEl.value || '').trim()`, so with the element gone it would evaluate to '',
       and the worker REBUILDS the whole entry (lib/vendor-ready-by.js: `note: String(it.note ||
       '').trim() || null`). The first save of any row would therefore have deleted the note stored
       on it. RW-1317-C's ECORE entry carries a real one from AIOPS — the ECORE-portal ship-date
       reading behind its vendor ETA — and that is exactly the kind of sentence nobody could
       reconstruct. */
    /* ⚠️ The date input holds a FIRM date only. A window's last day must not appear in it: it
       would read as a commitment the supplier never made, and saving the row back would store it
       as one. */
    h += '<input type="text" class="rqd-vendor-eta-label" placeholder="a window, e.g. early October" maxlength="80" value="' + esc(said) + '">';
    /* CUSTOM_READYBY_BY_CLAUDE (2026-09-16) — a stored range now puts its START here rather than
       nothing, so the pair round-trips: open the card, see the window you saved. ⛔ Still never
       `date` on a range — that is the window's LAST day, and it is the value this box is forbidden
       to show. */
    /* CUSTOM_READYBYUI_BY_CLAUDE (Ryan 2026-09-19) — "Start" / "End" IN PLACE OF mm/dd/yyyy.
       ⛔ A `type="date"` INPUT IGNORES `placeholder` — the browser owns that text and always draws
       its own format hint, so the obvious one-attribute fix does nothing at all. The working
       version is a class on the EMPTY state plus `::-webkit-datetime-edit { display: none }` and a
       `::before` carrying `data-ph` (dashboard.css). The native calendar picker is untouched,
       which is the half Ryan asked to keep.
       ⚠️ SO THE CLASS HAS TO BE KEPT IN STEP WITH THE VALUE, both here at render and in
       dashboard-main.js when somebody types — a stale `--empty` would hide a real date behind the
       word "Start", which is worse than the mm/dd/yyyy this replaces. */
    /* CUSTOM_CUSTOMERDATE_BY_CLAUDE - the box shows the SUPPLIER's date: a pre-2026-10-09 ECORE stamp stored the date + 1 day, and
       `vendorDate` (the board row's) takes that day back off, so a save from here stores the supplier's real date. */
    var _veStart = isUnparsed ? '' : (isRange ? ((ve && ve.start) || '') : ((ve && ve.vendorDate) || d));
    var _veEnd = isRange ? ((ve && ve.end) || '') : '';
    /* ⛔ THE WORD IS A SIBLING SPAN, NOT `::before` ON THE INPUT. First cut used
       `input::before { content: attr(data-ph) }` and nothing appeared: an `<input>` is a REPLACED
       element and does not render `::before` / `::after` in any browser. The rule was valid CSS,
       matched the element, and could never draw anything — which is why grepping the live bytes
       for it found it present and correct while the box stayed blank.
       ⛔ AND THE FIELD IS DIMMED, NOT HIDDEN. `display: none` on `::-webkit-datetime-edit`
       collapsed the input's content box, so the calendar picker — which lays out after it — slid
       to the left edge and dropped. `color: transparent` leaves the geometry exactly as it is, so
       the picker keeps the right-hand position it has on a filled box, which is the consistency
       Ryan asked for. */
    var _dateBox = function (val, ph, cls, extra) {
      return '<span class="rqd-eta-datewrap' + (val ? '' : ' rqd-eta-datewrap--empty') + '">'
        + '<input type="date"' + (extra || '') + ' class="' + cls
          + (val ? '' : ' rqd-vendor-eta-date--empty') + '" value="' + esc(val) + '">'
        + '<span class="rqd-eta-datewrap__ph">' + esc(ph) + '</span>'
        + '</span>';
    };
    h += _dateBox(_veStart, 'Start', 'rqd-vendor-eta-date', '');
    /* CUSTOM_READYBY_BY_CLAUDE (AIOPS b641360cdb02, Ryan 2026-09-16) — THE SECOND HALF OF A WINDOW.
       The API has taken `{start, end}` since the ready-by rewrite shipped and nothing in the portal
       could send one, so the only way to record "the 6th to the 13th" was to write it as words and
       hope the parser found a month in them. Fill both and the row stores a range; fill only the
       first and it is a firm date, exactly as before.
       ⛔ THE RANGE'S OWN DATES, NOT `date`. A range entry keeps `date` at its LAST day for the four
       readers that key on it, so echoing `date` into this box would make every saved window one day
       wide on the next load. */
    /* CUSTOM_READYBYUI_BY_CLAUDE (Ryan 2026-09-19) — the "to" between the boxes is GONE; the two
       placeholders say which is which now, which is what it was there to do. Its tooltip ("leave
       this empty for a firm date") moves onto the End box itself, where the rule actually applies. */
    /* ⚠️ BOTH CLASSES. `rqd-vendor-eta-date` is what carries this input's geometry, its sold-deal
       tint, its dark skin and its focus ring — every one of those lives in a selector LIST in
       dashboard.css, and a lookalike class would have had to be added to eight of them and would
       have drifted out of one. `date2` is the behavioural hook and nothing else.
       ⚠️ `querySelector('.rqd-vendor-eta-date')` still finds the START box, because it returns the
       first match in document order and this one is second. */
    h += _dateBox(_veEnd, 'End', 'rqd-vendor-eta-date rqd-vendor-eta-date2',
      ' title="Leave this empty for a firm date. Fill it in and the row records a window — the supplier has not committed to a day."');
    h += '</div>';
    h += '</div>';
    return h;
  }

  /* CUSTOM_CUSTWINDOW_BY_CLAUDE (Ryan 2026-10-02, RW-1380-B) — CUSTOMER WINDOW, the customer's own
     DELIVERY window. Ryan: "a 'do not ship before' ... and a 'do not ship after' ... technically these
     will be delivery dates". RW-1380-B asked for delivery after the 17th, so Earliest = the 18th.

     ⛔ ONE ROW PER DEAL, ABOVE EVERY SUPPLIER. The customer has one calendar and every shipment lands
     at the same door, which is the opposite of Vendor ready (a dock date per supplier).
     ⛔ DELIVERY DATES, BOTH DAYS INCLUDED — not ship dates. Working back 2-6 days of transit to a
     pickup is the desk's call; this row only records what the customer said.

     Drawn from the Vendor ready row's own classes (check circle, date boxes, ghost-when-set, n/a once
     everything shipped) so the two rows cannot drift apart. ⚠️ `rqd-cust-window-row` is what keeps
     its boxes OUT of saveVendorEta: dashboard-main.js routes any change inside it to
     saveCustomerWindow instead. Both dates are optional; worker: lib/customer-window.js. */
  /* CUSTOM_SUPPWINDOW_BY_CLAUDE (Ryan 2026-10-09, SHOP-2128) - a supplier's OWN window, when one is saved
     (`q.customerWindows`, keyed by the supplier name this card writes). None = the order's window applies. */
  function supplierWindowOf(q, vendor) {
    var map = q && q.customerWindows;
    if (!vendor || !map || typeof map !== 'object') return null;
    if (map[vendor] && typeof map[vendor] === 'object') return map[vendor];
    var k = Object.keys(map).find(function (kk) { return vendorNamesMatch && vendorNamesMatch(kk, vendor); });
    return (k && map[k] && typeof map[k] === 'object') ? map[k] : null;
  }

  /* CUSTOM_NOTICEEXTRAS_BY_CLAUDE (ONYX 16e6cf0a937f, Ryan 2026-10-16, JW-1276-E) - a rep asked for something extra in the
     customer's shipped email (worker POST /deal-notice-extras; `q.noticeExtras`). Read-only: one row per request, ticked once the
     shipped email went, saying whether it carried it. The reason rides the hover. */
  function repRequestRowsHtml(q) {
    var map = q && q.noticeExtras;
    if (!map || typeof map !== 'object') return '';
    var h = '';
    Object.keys(map).forEach(function (k) {
      var r = map[k];
      if (!r || !Array.isArray(r.include) || !r.include.length) return;
      var what = r.include.map(function (x) { return x === 'actual-weights' ? 'actual weights' : String(x); }).join(', ');
      var done = !!r.honoredAt;
      var day = function (iso) { return iso ? formatDate(String(iso).slice(0, 10)) : ''; };
      var status = done
        ? (r.honoredHow === 'included' ? 'in the shipped email ' : 'shipped email went without them ') + day(r.honoredAt)
        : 'asked ' + day(r.at);
      h += '<div class="rqd-vendor-item rqd-vendor-eta-row rqd-rep-request-row"' + (r.reason ? ' title="' + esc(r.reason) + '"' : '') + '>';
      h += '<div class="rqd-vendors__check' + (done ? ' rqd-vendors__check--done' : '') + '" style="pointer-events:none;cursor:default;"><span style="font-size:12px;">' + (done ? '\u2713' : '') + '</span></div>';
      h += '<div class="rqd-vendor-item__namegroup">';
      h += '<span class="rqd-vendor-item__name">Rep request</span>';
      h += '<span class="rqd-toggle__date">' + esc((r.requestedBy || 'Rep') + ': ' + what + ' in the shipped email' + (k !== '*' ? ' (' + k + ')' : '')) + '</span>';
      h += '<span class="rqd-toggle__date">' + esc(status) + '</span>';
      h += '</div></div>';
    });
    return h;
  }

  function customerWindowRowHtml(q, shipped, vendor) {
    var ow = (q.customerWindow && typeof q.customerWindow === 'object') ? q.customerWindow : null;
    /* CUSTOM_SUPPWINDOW_BY_CLAUDE - with `vendor` this is that supplier's row: its own window, or the order's
       shown in grey as the placeholder ("same as order") while it has none. */
    var w = vendor ? supplierWindowOf(q, vendor) : ow;
    var inherited = (vendor && !w && ow && (ow.earliest || ow.latest)) ? ow : null;
    /* CUSTOM_WINDOWGHOST_BY_CLAUDE (Ryan 2026-10-09) - the two rows answer each other:
         - a supplier row with no window of its own SHOWS the order's dates, faded like any answered row,
           with "same as order" (still editable: typing gives that supplier its own window);
         - the order row reads n/a, faded, only once EVERY supplier has its own window (then nothing uses it).
       `n/a` keeps its Deal Dashboard meaning: not needed. */
    var _sups = (q.financials && Array.isArray(q.financials.vendors)) ? q.financials.vendors : [];
    var allCovered = !vendor && _sups.length > 1 && _sups.every(function (sv) { return !!supplierWindowOf(q, sv); });
    var own = !!(w && (w.earliest || w.latest));
    var _winShown = own ? w : (inherited || null);
    var e = (_winShown && _winShown.earliest) || '';
    var l = (_winShown && _winShown.latest) || '';
    var set = !!(e || l);
    var naCovered = allCovered;
    var na = naCovered || (!set && !!shipped);
    var answered = set && !na;
    var naTitle = naCovered ? 'Not used: each supplier has its own customer window.' : 'Everything shipped without a customer window, so it is no longer needed.';
    var h = '<div class="rqd-vendor-item rqd-vendor-eta-row rqd-cust-window-row' + (vendor ? ' rqd-cust-window-row--supplier' : '') + (inherited && !own ? ' rqd-cust-window-row--inherited' : '') + (na ? ' rqd-vendor-eta-row--na' : '') + (answered ? ' rqd-vendor-eta-row--answered' : '') + '" data-ref="' + esc(q.reference) + '"'
      + (vendor ? ' data-window-vendor="' + esc(vendor) + '"' : '') // never data-vendor: the Vendor ready rows and their handlers key on that
      + (shipped ? ' data-shipped="1"' : '')
      + (na ? ' title="' + esc(naTitle) + '"' : '') + '>';
    h += '<div class="rqd-vendors__check' + ((answered || na) ? ' rqd-vendors__check--done' : '') + (na ? ' rqd-toggle__check--na' : '') + '" style="pointer-events:none;cursor:default;"><span style="font-size:' + (na ? '8px' : '12px') + ';">' + (na ? 'n/a' : (answered ? '\u2713' : '')) + '</span></div>';
    h += '<div class="rqd-vendor-item__namegroup">';
    h += '<span class="rqd-vendor-item__name" title="The customer\u2019s DELIVERY window: the first and last days they can receive it, both days included. Not a ship date: time the pickup so transit lands it inside the window. Earliest only = that day or later. Latest only = by that day. Leave both empty when the customer has no constraint.">Customer window</span>';
    if (own && w.at && !naCovered) {
      h += '<span class="rqd-toggle__date" title="When the window was last saved' + (w.by ? ', by ' + esc(w.by) : '') + '.">updated ' + formatDate(String(w.at).slice(0, 10)) + '</span>';
    }
    /* CUSTOM_QUOTEDWINDOW_BY_CLAUDE (ONYX 0b837ff9363b, Ryan 2026-10-27) - the order window's team-only note, typed in the Quote
       Workstation's Sold To card (read-only here, so the row stays two date boxes; the worker keeps it on a dates-only save). */
    if (!vendor && own && w.note && !naCovered) {
      h += '<span class="rqd-toggle__date rqd-cust-window-note" title="Team-only note, never emailed to the customer. Edit it in the Quote Workstation (Sold To card).">' + esc(w.note) + '</span>';
    }
    if (vendor && inherited && !own) {
      h += '<span class="rqd-toggle__date rqd-cust-window-inherit" title="This supplier has no window of its own, so the order\u2019s window applies. Change a date to give this supplier its own.">same as order</span>';
    }
    if (naCovered) {
      h += '<span class="rqd-toggle__date">each supplier has its own</span>';
    }
    h += '</div>';
    h += '<div class="rqd-vendor-row-controls">';
    h += '<span class="rqd-toggle__saved rqd-cust-window-saved"></span>';
    var box = function (val, ph, cls, title) {
      return '<span class="rqd-eta-datewrap' + (val ? '' : ' rqd-eta-datewrap--empty') + '">'
        + '<input type="date" class="rqd-vendor-eta-date ' + cls + (val ? '' : ' rqd-vendor-eta-date--empty') + '" value="' + esc(val) + '" title="' + esc(title) + '">'
        + '<span class="rqd-eta-datewrap__ph">' + esc(ph) + '</span>'
        + '</span>';
    };
    h += box(e, 'Earliest', 'rqd-cust-window-earliest', 'First day the customer can take delivery (included). Leave empty if any day up to Latest is fine.');
    h += box(l, 'Latest', 'rqd-cust-window-latest', 'Last day the customer can take delivery (included). Leave empty if any day from Earliest on is fine.');
    h += '</div>';
    h += '</div>';
    return h;
  }
  /* END CUSTOM_CUSTWINDOW_BY_CLAUDE */

  /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (AIOPS be87acc13b79, Ryan 2026-09-26) — the ordering key a vendor row
     reads and writes. Lifted verbatim out of the vendors.forEach below so the header's "(n/n)" count
     and the rows resolve the same row; the two comments inside are the history of the rule. */
  function trackingRowKey(ordering, v) {
    var vKey = v;
    if (!Object.prototype.hasOwnProperty.call(ordering, v)) {
      for (var _vk in ordering) {
        if (Object.prototype.hasOwnProperty.call(ordering, _vk) && vendorNamesMatch(v, _vk)) { vKey = _vk; break; }
      }
    } else if (!String((ordering[v] && ordering[v].tracking) || '').trim()) {
      /* CUSTOM_TOLDCHECK_BY_CLAUDE (AIOPS `40c3547e465d`, Ryan 2026-09-26 - RW-1317-C) — AN EXACT ROW
         WITH NO NUMBER DOES NOT HIDE AN ALIAS ROW THAT HAS ONE. RW-1317-C carries BOTH `ordering["ECORE"]`
         (dims only) and `ordering["ECORE Athletic"]` (XPO PRO 112461484); the exact-first rule above picked
         the empty one and the card showed four blank boxes. When the exact row carries no tracking, a
         matching row that DOES is the storage key, so the reads, the × and the notify buttons all land on
         the row that owns the number. With no stamped alias the exact row stands, as before. */
      for (var _vk2 in ordering) {
        if (_vk2 !== v && Object.prototype.hasOwnProperty.call(ordering, _vk2) && vendorNamesMatch(v, _vk2)
          && String((ordering[_vk2] && ordering[_vk2].tracking) || '').trim()) { vKey = _vk2; break; }
      }
    }
    return vKey;
  }

  /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (AIOPS be87acc13b79, Ryan 2026-09-26 - RW-1317-C) — the row's ✓.
     Ryan's answer on the card: "told OR delivered". The June rule stands — typing a tracking number
     does not tick a row, the customer being told does — and a carrier-confirmed delivery now ticks it
     too, because the customer has the goods whether or not the shipping email went out (RW-1317-C:
     PowerBlock delivered 9/19, its notice only ever scheduled). The tick ONLY: the row's lock, its
     Send-to-Customer button and the header's shipped gate read their own facts, so a delivered row
     with no notice can still be told. */
  function trackingRowTicked(q, ordering, vKey) {
    var ord = ordering[vKey] || {};
    if (ord.deliveredAt) return true;
    if (q.source === 'online') return !!ord.shopifyFulfillmentId;
    return !!((((q.customerShippingEmails || {})[vKey]) || {}).at || ord.customerNotifiedAt || ord.shippedNoTracking);
  }

  function buildShippingTrackingCard(q, opts) {
    opts = opts || {};
    // Gate: admin-only, sold, not a linked-child cross-source row.
    if (!opts.effectiveAdmin) return '';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) — a REPLACEMENT reaches this surface too.
       Goods-committed, not sold: see RQD.dealGoodsAreCommitted in dashboard-claim-card.js for
       why the money surfaces (AR/AP, invoicing, commission) still ask the old question. */
    if (!window.RQD.dealGoodsAreCommitted(q)) return '';
    if (q.linkedParentRef) return '';

    var vendors = (q.financials || {}).vendors || [];
    if (vendors.length === 0) return '';

    var renderedFulfillmentEarly = !!opts.renderedFulfillmentEarly;
    var html = '';
    var ordering = q.ordering || {};
    var ordering = q.ordering || {};
    var isOnlineOrder = q.source === 'online';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Per-bucket shipping mode. Mixed-cart
       online orders (single vendor, 2+ buckets, e.g., SHOP-1681's
       broker-freight + supplier-parcel split) replace the single vendor
       row with one tracking row per bucket. Each row's Mark Fulfilled
       fires a Shopify partial fulfillment scoped to that bucket's line
       items → customer receives a separate "Your order has shipped"
       email per bucket. State lives on bucket.shipping (not
       lc.ordering[vendor]) so the broker can ship today and the parcel
       ships next week independently. */
    var useBucketShipping = isOnlineOrder
      && vendors.length === 1
      && Array.isArray(q.shippingBuckets)
      && q.shippingBuckets.length > 1;
    // Shipping done = every vendor has shipped. Online → shopifyFulfillmentId
    // (set by Mark Fulfilled). Offline → ord.ordered (auto-set when carrier
    // + type + tracking number are all filled). Bucket mode → every bucket
    // has bucket.shipping.fulfillmentId.
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-18) — On offline-SOLD
       deals, the card hosts an additional "Weight/dims received"
       row (pallet entry form, same UX as the Freight Balance card
       on online Flow A) so freight data accumulates into the
       sku-freight corpus that trains the AI freight estimator.
       Card title flips Tracking → Fulfillment when that row is
       present, and dims-saved becomes part of the "all done" gate
       alongside vendor fulfillment. */
    var _isOfflineSold = !isOnlineOrder && q.lifecycleStatus === 'sold';
    var _fbDimsForCard = (q.freightBalance && q.freightBalance.dims) || null;
    var _fbDimsPalletsForCard = null;
    if (_fbDimsForCard && typeof _fbDimsForCard === 'object') {
      _fbDimsPalletsForCard = (Array.isArray(_fbDimsForCard.groups) && _fbDimsForCard.groups.length > 0)
        ? _fbDimsForCard.groups
        : (Array.isArray(_fbDimsForCard.pallets) ? _fbDimsForCard.pallets : null);
    }
    var _dimsLoggedForCard = Array.isArray(_fbDimsPalletsForCard) && _fbDimsPalletsForCard.length > 0;
    var _dimsGateActive = _isOfflineSold;
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Per-vendor shipped
       check uses the shared vendorIsShipped predicate so this card
       agrees with the status tracker bar + To Do emoji strip. Was
       previously using ord.ordered for offline (legacy flag), which
       could drift from the data-purity check (carrier + trackingType
       + tracking all present) the other surfaces use. Bucket-mode
       aggregate stays bucket-level (different layer — buckets vs
       vendors). */
    var _vendorShipAllDone = useBucketShipping
      ? q.shippingBuckets.every(function (b) { return !!(b && b.shipping && b.shipping.fulfillmentId); })
      : vendors.every(function (v) { return vendorIsShipped(q, v); });
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Dims capture on
       offline-sold orders is now OPT-IN (admin clicks "+ Log freight
       dims" to expose the entry form). For orders where the vendor
       ships with their own carrier, the supplier doesn't share dims
       with us so the input was just visual noise. For orders where
       WE manage shipping via our broker, admin opens the form on
       demand. Either way, dims no longer gate "shipping done" — that
       gate becomes purely vendor-shipping-done. */
    var _shipAllDone = _vendorShipAllDone;
    /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (AIOPS be87acc13b79, Ryan 2026-09-26 - RW-1317-C) — "FULFILLMENT (2/3)",
       counting the rows' own ✓ so the bracket matches the boxes under it. Per-vendor mode only; the
       bucket mode is one supplier split into shipments and keeps its single tick.
       ⛔ On a multi-vendor card the header ✓ now ALSO needs every row ticked: "(2/3) ✓" would be the
       header contradicting itself, which is the one thing a count in the title must never do. */
    var _rowsTicked = useBucketShipping ? 0 : vendors.filter(function (_tv) {
      return trackingRowTicked(q, ordering, trackingRowKey(ordering, _tv));
    }).length;
    if (!useBucketShipping && vendors.length > 1 && _rowsTicked < vendors.length) _shipAllDone = false;
    /* END CUSTOM_DDCARDCOUNT_BY_CLAUDE */
    /* CUSTOM_DDCARDCOUNT_BY_CLAUDE — the card's <div> now opens below, once its title is known: the
       title carries a live count, so the card declares its collapse id ("tracking" / "fulfillment",
       exactly what the title slug gave it before) instead of having it read off the heading. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Renamed Shipping → Tracking per Ryan.
       "Shipping" was ambiguous next to the Fulfillment card; this card's
       actual content is carrier + tracking number + Mark Fulfilled (which
       pushes tracking to Shopify and triggers the customer email), so
       "Tracking" reads more accurately.
       Ryan 2026-05-18 — When the dims gate is active (offline + sold)
       the card's responsibility expands beyond pure tracking to also
       include the dims/weight capture step, so the title flips to
       "Fulfillment" to reflect the broader scope. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Title flips to
       "Fulfillment" when ANY dims have been logged — either the
       legacy global lifecycle.freightBalance.dims OR the new
       per-vendor ordering[vendor].freightDims. */
    var _anyVendorDimsLogged = vendors.some(function (_vd) {
      var _od = (ordering[_vd] || {}).freightDims;
      if (!_od || typeof _od !== 'object') return false;
      return (Array.isArray(_od.groups) && _od.groups.length > 0)
        || (Array.isArray(_od.pallets) && _od.pallets.length > 0);
    });
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — When the upper
       bucket-aware Fulfillment card already rendered (set by
       renderedFulfillmentEarly above), keep THIS card's title as
       "Tracking" so we don't have two cards labeled FULFILLMENT
       stacked on top of each other. The upper card owns the
       freight handshake / dims / PO workflow; this card is purely
       tracking entry + Mark Fulfilled. */
    var _titleForCard = ((_dimsLoggedForCard || _anyVendorDimsLogged) && !renderedFulfillmentEarly) ? 'Fulfillment' : 'Tracking';
    html += '<div class="rqd-detail__arap" data-section-id="' + _titleForCard.toLowerCase() + '">'; // CUSTOM_DDCARDCOUNT_BY_CLAUDE
    html += (useBucketShipping || typeof window.RQD.sectionTitleWithCount !== 'function')
      ? sectionTitleHtml(_titleForCard, _shipAllDone)
      : window.RQD.sectionTitleWithCount(_titleForCard, _shipAllDone, _rowsTicked, vendors.length);
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_PAYTERMSGATE_BY_CLAUDE (AIOPS `87238b70d3ee`, Ryan 2026-09-18) — the terms + balance
       row, at the TOP of the card the pickup is arranged on. Same verdict, same markup, same one
       function as the Purchasing card's copy: two cards stating a money rule in their own words is
       how they start disagreeing. Renders nothing when the money is settled. */
    if (window.RQD && window.RQD.renderPaymentGateRow) html += window.RQD.renderPaymentGateRow(q);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Per-vendor dims
       refactor. Global card-level "+ Log freight dims" was unambiguous
       for single-vendor orders but for multi-vendor orders there was
       no way to know which vendor's shipment the dims belonged to.
       Moved dims capture INTO each vendor's tracking row below
       (rendered inside the vendors.forEach loop). Legacy
       lifecycle.freightBalance.dims (global) still readable via the
       worker — new captures go to lifecycle.ordering[vendor].freightDims.
       Global card-level block removed. */
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    // Carrier list — online orders mirror Shopify's full US-relevant
    // fulfillment-screen dropdown (~50 carriers). The dashboard renders
    // it as a search-as-you-type <input list="..."> + <datalist> so
    // typing "fed" instantly filters to FedEx variants.
    //
    // Substring matching in the worker's normalizeCarrierForShopify maps
    // each entry back to Shopify's canonical carrier name when
    // fulfillmentCreate fires. Unknown free-typed carriers pass through
    // as-is and the tracking URL becomes the customer's link.
    //
    // Offline orders keep the shorter LTL-focused list (no Shopify sync,
    // so the carrier name is informational only).
    // Single carrier source for both online + offline orders. Was
    // a short LTL-only list for offline, but the autocomplete
    // input + filter-as-you-type means a longer list reads the
    // same when admin types "fed" / "ups" / etc. Removes the
    // <select>+Other-input split that offline had.
    // (carriers comes from module-scope, populated by initShippingTrackingCard
    // with ONLINE_CARRIERS from main — see header above.)
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Expanded type list.
       Carriers use a grab bag of names for the customer-trackable
       identifier — PRO# (LTL), BOL# (heavy freight), Shipment ID
       (FedEx Freight new portal), Reference # (XPO), Pickup #
       (some 3PLs), Order # (parcel-blend). 'Other' is the
       free-text escape for anything not in the list. */
    var trackingTypes = ['Tracking #', 'PRO #', 'BOL #', 'Shipment #', 'Shipment ID', 'Reference #', 'Pickup #', 'PO #', 'Order #', 'Other'];
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    // Gate each vendor's tracking row on "PO sent" — same condition the
    // Purchasing card uses to flip its checkbox. A vendor's PO counts
    // as sent when any non-voided PO has status='sent' OR has been
    // downloaded. No PO yet → all tracking inputs + Send to Customer
    // disabled, with a title hint pointing the rep at the PO step.
    var shipPosByVendor = {};
    vendors.forEach(function (vv) { shipPosByVendor[vv] = []; });
    (q.pos || []).forEach(function (p) {
      if (!p || p.status === 'voided') return;
      var bucket = vendors.find(function (vv) { return vendorNamesMatch(vv, p.vendor); });
      if (bucket) shipPosByVendor[bucket].push(p);
    });
    function isPoSentForVendor(vn) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Direct-purchased
         vendors count as "PO sent" for the Tracking card's input-lock
         gate (otherwise carrier/type/tracking inputs render disabled
         because the vendor never had a real PO). Admin still needs to
         enter tracking once the supplier ships — those inputs must be
         enabled. */
      var vo = (ordering || {})[vn] || {};
      if (vo.purchasedDirectly === true) return true;
      return (shipPosByVendor[vn] || []).some(function (p) { return p.status === 'sent' || !!p.downloadedAt; });
    }
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Bucket-mode shipping rows. For each
       bucket, render the same .rqd-vendor-item shape the vendor loop
       produces, but driven by bucket data: label = bucket.label,
       tracking state = bucket.shipping, po-sent gate = bucket's own
       PO record, data-bucket attribute threads through to the
       handshake → /shopify-fulfillment-create call. */
    html += repRequestRowsHtml(q); // CUSTOM_NOTICEEXTRAS_BY_CLAUDE - a rep's request for the shipped email, above the window
    html += customerWindowRowHtml(q, _vendorShipAllDone); // CUSTOM_CUSTWINDOW_BY_CLAUDE - one per deal, above every supplier
    if (useBucketShipping) {
      var _bucketVendor = vendors[0];
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (2026-09-02) — bucket mode is ONE vendor split across
         several shipments, so the ready-date is one row above the buckets rather than one per
         bucket: the supplier's dock date is a fact about the supplier, not about how we chose to
         split the freight. */
      html += vendorEtaRowHtml(q, _bucketVendor, _vendorShipAllDone); // CUSTOM_DDCARDCOUNT_BY_CLAUDE - one supplier: every bucket out
      q.shippingBuckets.forEach(function (b) {
        var _bId = b.id;
        var _bLabel = b.label || _bId;
        var _bShipping = b.shipping || {};
        var _bFulfilled = !!_bShipping.fulfillmentId;
        // Per-bucket PO-sent: the bucket's own (non-voided) latest PO
        // must be status='sent' or have downloadedAt.
        var _bucketPos = (q.pos || []).filter(function (p) { return p && p.bucket === _bId && p.status !== 'voided'; });
        var _bucketPoSent = _bucketPos.some(function (p) { return p.status === 'sent' || !!p.downloadedAt; });
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Surface the bucket's PO number(s)
           inline next to the bucket label so reps can match incoming
           supplier tracking emails ("re: SHOP-1689.1 — here's tracking")
           to the right tracking input. Only count POs that were
           actually sent or downloaded (skip drafts in flight). On
           regen scenarios with multiple sent POs (e.g., .1 + .2),
           show all so a supplier reply against either number is
           visually paired. Voided already filtered above. */
        var _bucketPoNumbers = _bucketPos
          .filter(function (p) { return p.status === 'sent' || !!p.downloadedAt; })
          .map(function (p) { return p.poNumber; })
          .filter(Boolean);
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        var _bLockTitle = _bFulfilled
          ? 'Fulfilled in Shopify — tracking is locked.'
          : (_bucketPoSent ? '' : 'Send the ' + _bLabel + ' PO first — shipping is gated on Purchasing.');
        var _bLockAttrs = (_bFulfilled || !_bucketPoSent) ? ' disabled title="' + esc(_bLockTitle) + '"' : '';
        var _bCarrier = _bShipping.carrier || '';
        var _bTracking = _bShipping.trackingNumber || '';
        var _bTrackingUrl = _bShipping.trackingUrl || '';
        var _bDate = _bShipping.fulfilledAt ? formatDate(_bShipping.fulfilledAt) : '';
        var _bHasData = !!(_bCarrier || _bTracking);
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) - PHASE 2: the step/state contract,
           extended from Purchasing and Fulfillment to this card so all three fulfillment
           surfaces answer the same questions the same way. Everything here used to live in a
           CSS class and a hover title - including WHY a row is locked, which is the one an
           operator actually needs and which a touch user can never see. */
        var _bStepState = _bFulfilled ? 'fulfilled'
          : (!_bucketPoSent ? 'awaiting-po' : (_bHasData ? 'in-transit' : 'pending'));
        html += '<div class="rqd-vendor-item"'
          + ' data-step="shipment" data-state="' + _bStepState + '"'
          + ' data-ref="' + esc(q.reference) + '" data-bucket="' + esc(_bId) + '"'
          + (_bLockTitle ? ' data-blocked-reason="' + esc(_bLockTitle) + '"' : '')
          + '>';
        html += '<div class="rqd-vendors__check' + (_bFulfilled ? ' rqd-vendors__check--done' : '') + '" data-ref="' + esc(q.reference) + '" data-bucket="' + esc(_bId) + '" style="pointer-events:none;cursor:default;"><span style="font-size:12px;">' + (_bFulfilled ? '✓' : '') + '</span></div>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Bucket's sent PO number(s) appended
           to the label as plain text (per Ryan: "regular text, not
           labels"). Format: "Supplier parcel - SHOP-1681.2-ECORE".
           Reps match incoming supplier tracking emails (which reference
           the PO #) to the right input row visually. Multiple POs per
           bucket (regen scenario) comma-joined. */
        var _bLabelWithPo = _bLabel + (_bucketPoNumbers.length > 0 ? ' - ' + _bucketPoNumbers.join(', ') : '');
        html += '<span class="rqd-vendor-item__name">' + esc(_bLabelWithPo) + '</span>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        if (_bDate) {
          html += '<span class="rqd-toggle__date">' + _bDate + '</span>';
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Undo inline after the bucket's
             fulfilled date (matches the vendor-row Undo). Cancels this bucket's Shopify
             fulfillment + clears wm.shippingBuckets[id].shipping, re-opening the row. */
          if (_bFulfilled) {
            html += '<span class="rqd-vendor-undo-sep" style="color:var(--rq-text-secondary);margin:0 3px;">-</span>';
            html += '<button type="button" class="rqd-vendor-undo-fulfill rqd-vendor-notify__undo" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(_bucketVendor) + '" data-bucket="' + esc(_bId) + '" data-order-name="' + esc(q.reference) + '" title="Undo — cancel this fulfillment in Shopify and re-open the tracking fields">Undo</button>';
          }
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Wrap the
           5 tracking controls + Mark Fulfilled button in a single
           row-controls container so they wrap as a coherent right-
           aligned cluster when the bucket label is long. Without
           this, items wrapped one-by-one with no cluster behavior:
           line 2 showed URL on the left and the sync button on the
           right with a gap between them. See .rqd-vendor-row-
           controls CSS rule. */
        html += '<div class="rqd-vendor-row-controls">';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        html += '<div class="rqd-vendor-tracking' + (_bucketPoSent ? '' : ' rqd-vendor-tracking--locked') + (_bFulfilled ? ' rqd-vendor-tracking--fulfilled' : '') + '"'
          + ' data-ref="' + esc(q.reference) + '"'
          + ' data-vendor="' + esc(_bucketVendor) + '"'
          + ' data-bucket="' + esc(_bId) + '"'
          + ' data-online="1"'
          + (_bFulfilled || !_bucketPoSent ? ' title="' + esc(_bLockTitle) + '"' : '') + '>';
        html += '<span class="rqd-vendor-tracking__status"></span>';
        html += '<button type="button" class="rqd-vendor-tracking__remove' + (_bHasData && !_bFulfilled ? ' rqd-vendor-tracking__remove--visible' : '') + '" data-ref="' + esc(q.reference) + '" data-bucket="' + esc(_bId) + '" title="Clear shipment" aria-label="Clear shipment">&times;</button>';
        html += '<div class="rqd-carrier-ac">';
        html += '<input class="rqd-vendor-tracking__carrier" type="text" placeholder="Carrier..." autocomplete="off" maxlength="60" value="' + esc(_bCarrier) + '"' + _bLockAttrs + '>';
        html += '<div class="rqd-carrier-ac__menu" role="listbox"></div>';
        html += '</div>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19, v2) — Tracking
           type dropdown for bucket-mode rows too. Each LTL carrier
           uses different terms for their identifier (PRO Number /
           BOL # / Shipment ID / etc.) — selecting the right label
           lets Abby write the customer email naturally ("tracked
           via {Carrier} using {Type}: {Number}"). Optional; falls
           back to generic "Tracking number" when blank. */
        var _bTrackingType = (_bShipping && _bShipping.trackingType) || '';
        var _bTypeIsOther = _bTrackingType === 'Other' || (_bTrackingType && trackingTypes.indexOf(_bTrackingType) === -1);
        var _bTypeVal = _bTypeIsOther ? 'Other' : _bTrackingType;
        html += '<select class="rqd-vendor-tracking__type"' + _bLockAttrs + '>';
        html += '<option value=""' + (!_bTypeVal ? ' selected' : '') + '>-- Type --</option>';
        for (var _bti = 0; _bti < trackingTypes.length; _bti++) {
          html += '<option value="' + trackingTypes[_bti] + '"' + (_bTypeVal === trackingTypes[_bti] ? ' selected' : '') + '>' + trackingTypes[_bti] + '</option>';
        }
        html += '</select>';
        html += '<input class="rqd-vendor-tracking__type-other" placeholder="Type label" maxlength="20" value="' + esc(_bTypeIsOther && _bTrackingType !== 'Other' ? _bTrackingType : ((_bShipping && _bShipping.trackingTypeOther) || '')) + '"' + (_bTypeIsOther ? '' : ' style="display:none"') + _bLockAttrs + '>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        html += '<input class="rqd-vendor-tracking__input" placeholder="Number" maxlength="30" value="' + esc(_bTracking) + '"' + _bLockAttrs + '>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — URL field is
           now ALWAYS visible (was conditional on custom carrier).
           Reason: known-carrier auto-built URLs occasionally break
           (e.g. FedEx Freight migrating to a new tracking site
           2026-06), and admin needs to override per-shipment.
           When provided, the URL wins over Shopify's auto-build. */
        html += '<input class="rqd-vendor-tracking__url" placeholder="Tracking URL — optional, auto-linked for known carriers" title="Leave blank for any carrier we already know (FedEx, UPS, Daylight, Ward, ABF, Estes, SAIA, ODFL, R+L, XPO, TForce...) — the customer email builds the deep link from the tracking/PRO number. Fill this in only to OVERRIDE that, or for a carrier we have no link for yet." maxlength="500" value="' + esc(_bTrackingUrl) + '"' + _bLockAttrs + '>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        html += '</div>';
        html += '<div class="rqd-vendor-shopify-sync">';
        if (_bFulfilled) {
          // (Undo lives inline after the fulfilled date — see the namegroup above.)
          html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link rqd-vendor-tracking__sync--fulfilled" disabled title="Fulfilled in Shopify">Fulfilled ✓</button>';
        } else if (!_bucketPoSent) {
          html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link rqd-invoice__link--disabled" disabled title="' + esc(_bLockTitle) + '">Mark as Fulfilled</button>';
        } else {
          html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link"'
            + ' data-ref="' + esc(q.reference) + '"'
            + ' data-vendor="' + esc(_bucketVendor) + '"'
            + ' data-bucket="' + esc(_bId) + '"'
            + ' data-order-name="' + esc(q.reference) + '"'
            + ' style="cursor:pointer;">Mark as Fulfilled</button>';
        }
        html += '</div>';  // close .rqd-vendor-shopify-sync
        html += '</div>';  // close .rqd-vendor-row-controls (Ryan 2026-05-22)
        html += '</div>';  // close .rqd-vendor-item
      });
    } else
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    vendors.forEach(function(v) {
      /* CUSTOM_OFFLINECLEAR_BY_CLAUDE (AIOPS `6351c8516cbf`, Ryan 2026-09-24 - RW-1285-C) — THE ROW THE
         SHIPMENT IS STORED UNDER, NOT THE QUOTE'S SPELLING OF THE SUPPLIER.
         RW-1285-C quotes "ECORE" and carries its TForce PRO under `ordering["ECORE Athletic"]` (the agent's
         stamp resolved the Guide name). `vendorIsShipped` already resolves the name, so the header lit its
         ✓; this card read `ordering["ECORE"]` exactly, drew four empty boxes, and the × then wrote a blank
         "ECORE" sibling beside the real row, so the wrong number could not be seen OR cleared.
         ⛔ `vKey` IS THE STORAGE KEY and it rides every data-vendor below, so the reads here and the writes
         behind the buttons land on the same row. It differs from `v` ONLY when no exact row exists and an
         alias row does; everywhere else it is `v` and nothing changes. The Vendor ready row and the
         "quoted as" aside keep `v` — vendorEta and the label are about the quote's supplier name. */
      var vKey = trackingRowKey(ordering, v); // CUSTOM_DDCARDCOUNT_BY_CLAUDE - the rule moved into trackingRowKey below, unchanged
      var ord = ordering[vKey] || {};
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — the supplier this row is really about.
         Ryan: *"down below on the tracking card, I also need it to update 'Blue Sky Fitness
         Supply' there with the [Actual Brand Name] too."* This card is a list of people whose
         shipments you are waiting on, so a white-label row naming US was naming the one party
         who is definitely not shipping anything.

         ⛔ LABEL ONLY. `v` stays on all eleven data-vendor attributes below, because
         ordering[], the shipment record, the dims block and the notify/fulfil handlers are all
         keyed on the quote-line name. Resolving the KEY here would point this card's writes at a
         row the Purchasing card cannot see.

         Falls back to `v` whenever nothing resolves, so ordinary vendors are untouched. */
      var _vDisplay = (window.RPDash && window.RPDash.resolvedVendorFor)
        ? (window.RPDash.resolvedVendorFor(q, v) || v)
        : v;
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Self-healing
         data-driven checkbox. ord.ordered carried both "tracking
         entered" AND legacy "PO sent / mark purchased" semantics on
         different code paths. Reading it directly produced stale ✓
         marks on direct-purchased vendors (no tracking entered) and
         kept showing them even after the worker stopped writing
         ordered:true on direct purchase.
         CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-04) — The ✓ now reflects
         CUSTOMER-NOTIFIED, not merely tracking entered: an offline row ticks only
         once the customer was actually emailed (Send to Customer) / Mark Notified,
         or the shipment was fulfilled without tracking. Entering tracking and
         opening the notify pop-up no longer ticks it on its own. Online still
         uses the Shopify fulfillment id (authoritative). */
      var isOnlineVRow = q.source === 'online';
      var isDone = trackingRowTicked(q, ordering, vKey); // CUSTOM_DDCARDCOUNT_BY_CLAUDE - was: online = fulfillment id; offline = told (vKey: CUSTOM_OFFLINECLEAR_BY_CLAUDE)
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      var dateStr = ord.date ? formatDate(ord.date) : '';
      // Online-order rows expose two extra fields:
      //   - tracking URL (required when carrier = Other)
      //   - "Send to Customer" button (visible once carrier + number are
      //     present); flips to "Synced ✓" once Shopify has the
      //     fulfillment recorded for this vendor.
      var shopifyFulfillmentId = ord.shopifyFulfillmentId || '';
      var trackingUrl = ord.trackingUrl || '';
      var poSent = isPoSentForVendor(v);
      // Three input-lock states for online rows (precedence order):
      //   1. Fulfilled in Shopify → permanent lock + green tint, no further edits
      //   2. PO not yet sent      → temporary lock until Purchasing fires
      //   3. PO sent, not fulfilled → editable
      // Offline rows don't have a Shopify fulfillment step; their locks
      // come from PO-not-sent only.
      var isFulfilled = !!shopifyFulfillmentId && isOnlineOrder;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — Offline "completed" =
         customer emailed (Notify done). Once sent, the offline tracking row
         ghosts + locks + shows a "Fulfilled ✓" indicator, mirroring online's
         post-fulfillment state. */
      var _cse = (q.customerShippingEmails && q.customerShippingEmails[vKey]) || {}; // vKey: CUSTOM_OFFLINECLEAR_BY_CLAUDE
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — Dropped the old
         `!q.linkedOnlineOrderName` guard: paid-online offline orders now go
         through the same offline notify flow (they set the customerShippingEmails
         marker too), so they must ghost/lock the inputs on notify just like pure
         offline rows. The button already keyed off _notifiedAt (no guard), so
         keeping the guard here left the button ghosted but the inputs active. */
      var offlineNotified = !isOnlineOrder && !!(_cse.at || ord.customerNotifiedAt);
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — Offline shipment marked
         fulfilled WITHOUT a tracking number (customer pickup / tracking
         unavailable). Completes + locks the row like a notified one, but no
         email was sent — so it reads "Fulfilled · no tracking" with an Undo. */
      var offlineNoTrack = !isOnlineOrder && !!ord.shippedNoTracking;
      /* CUSTOM_OFFLINECLEAR_BY_CLAUDE (AIOPS `2e3e604e835c`) — a notified offline row locks only while it
         CARRIES a number. After a clear the customer-told marker stays (it is still true, and it is what
         stops a second "shipped" email), so locking on the marker alone would leave the row with no way to
         type the right number in. */
      var offlineNotifiedLocked = offlineNotified && !!ord.tracking;
      var isCompleted = isFulfilled || offlineNotifiedLocked || offlineNoTrack;
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      var lockTitle = isFulfilled
        ? 'Fulfilled in Shopify — tracking is locked.'
        : (offlineNotifiedLocked
          ? 'Customer emailed — tracking is locked.'
          : (offlineNoTrack
            ? 'Marked fulfilled without tracking — locked.'
            : (poSent ? '' : 'Send the PO to ' + _vDisplay + ' first — shipping is gated on Purchasing.')));
      var lockAttrs = (isCompleted || !poSent) ? ' disabled title="' + esc(lockTitle) + '"' : '';
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) - PHASE 2: the step/state contract,
         extended from Purchasing and Fulfillment to this card so all three fulfillment
         surfaces answer the same questions the same way. Everything here used to live in a
         CSS class and a hover title - including WHY a row is locked, which is the one an
         operator actually needs and which a touch user can never see. */
      var _vStepState = isCompleted ? 'fulfilled'
        /* ord is this vendor's ordering record (declared ~L389). Using it directly rather than
           hasAnyTrackingData, which is a var declared BELOW this line and would hoist to undefined. */
        : (!poSent ? 'awaiting-po' : ((ord.carrier || ord.tracking) ? 'in-transit' : 'pending'));
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-02) — this vendor's ready-date, emitted as its
         OWN row immediately above the vendor's row.

         ⛔ BEFORE `.rqd-vendor-item` OPENS, NOT INSIDE IT. Placed within the vendor row it nested a
         second `.rqd-vendor-item` inside the first, and the two flattened into one line reading
         "[check] ECORE [check] Vendor ready [note] [date]" with the tracking inputs orphaned on a
         second row. Two sibling rows is the shape:

             [check] Vendor ready                  [note] [date]
             [check] ECORE       [carrier] [type] [number] [url]

         Inside the loop, so a two-vendor deal gets one pair per supplier. */
      /* CUSTOM_SUPPLIERHEADS_BY_CLAUDE (Ryan 2026-10-09, SHOP-2128) - with two or more suppliers the rows jumbled
         together ("Vendor ready", "ECORE", "Vendor ready", "Recycled Surfacing"). Each supplier now gets a heading
         like the Fulfillment card's, then its own Customer window, Vendor ready and Tracking rows. One supplier =
         the old layout exactly. */
      var _multiSup = vendors.length > 1;
      if (_multiSup) {
        html += '<div class="rqd-po-group rqd-trk__supplier-head" data-supplier-head="' + esc(v) + '">' + esc(_vDisplay)
          + (_vDisplay !== v ? ' <span class="rqd-po-group__alias">quoted as ' + esc(v) + '</span>' : '') + '</div>';
        html += customerWindowRowHtml(q, isDone, v); // CUSTOM_SUPPWINDOW_BY_CLAUDE
      }
      html += vendorEtaRowHtml(q, v, isDone); // CUSTOM_DDCARDCOUNT_BY_CLAUDE - the shipment row's own tick
      html += '<div class="rqd-vendor-item"'
        + ' data-step="shipment" data-state="' + _vStepState + '"'
        + ' data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '"'
        + (lockTitle ? ' data-blocked-reason="' + esc(lockTitle) + '"' : '')
        + '>';
      // Checkbox + name + date. The read-only box AND the date beside the name
      // both reflect CUSTOMER-NOTIFIED (isDone) — neither shows until the
      // shipment email is sent / marked sent (or a no-tracking fulfill).
      html += '<div class="rqd-vendors__check' + (isDone ? ' rqd-vendors__check--done' : '') + '" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" style="pointer-events:none;cursor:default;"><span style="font-size:12px;">' + (isDone ? '✓' : '') + '</span></div>';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — name + date wrapped so
         the date sits right beside the name instead of being pushed across the
         row by the name's flex-grow. */
      html += '<div class="rqd-vendor-item__namegroup">';
      html += '<span class="rqd-vendor-item__name">' + esc(_multiSup ? 'Tracking' : _vDisplay) + '</span>'; // CUSTOM_SUPPLIERHEADS_BY_CLAUDE - the heading names the supplier
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — the quote name as an aside when the label
         above is a resolved supplier, so the row can still be tied back to the customer's quote
         and to the key its shipment state is stored under. Renders nothing for ordinary vendors. */
      if (!_multiSup && _vDisplay !== v) { // CUSTOM_SUPPLIERHEADS_BY_CLAUDE - with headings the aside rides the heading
        html += '<span class="rqd-toggle__date" title="Quoted as ' + esc(v) + ', shipped by '
          + esc(_vDisplay) + '">quoted as ' + esc(v) + '</span>';
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      if (dateStr && isDone) {
        html += '<span class="rqd-toggle__date">' + dateStr + '</span>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Undo inline right after the
           fulfilled date ("6/13/2026 - Undo"). Online fulfillments only. Cancels the
           Shopify fulfillment + clears the portal's shipping state, re-opening the row. */
        if (isOnlineOrder && shopifyFulfillmentId) {
          html += '<span class="rqd-vendor-undo-sep" style="color:var(--rq-text-secondary);margin:0 3px;">-</span>';
          html += '<button type="button" class="rqd-vendor-undo-fulfill rqd-vendor-notify__undo" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" data-order-name="' + esc(q.reference) + '" title="Undo — cancel this fulfillment in Shopify and re-open the tracking fields">Undo</button>';
        }
      }
      html += '</div>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Inline per-
         vendor dims trigger, positioned AFTER the date so it sits
         in the same slot as the Void link on Purchasing card rows.
         Styled like .rqd-freight-reset-link (italic, muted,
         dotted underline) so it visually matches that pattern.
         Saved state: compact `📦 {summary}` chip + Edit link.
         Unsaved state: `Log dims` link. Click expands the form
         into the empty .rqd-vendor-dims-block sub-row at the end
         of the vendor-item. Offline-sold only. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — build the dims trigger
         here but EMIT it on its own line below the name (see the
         .rqd-vendor-dims-line emit after row-controls) so it no longer competes
         with the tracking controls for line-1 width. */
      var _dimsLineHtml = '';
      if (!isOnlineOrder) {
        var _vDimsRaw = ord.freightDims || null;
        var _vDimsPallets = null;
        if (_vDimsRaw && typeof _vDimsRaw === 'object') {
          _vDimsPallets = (Array.isArray(_vDimsRaw.groups) && _vDimsRaw.groups.length > 0)
            ? _vDimsRaw.groups
            : (Array.isArray(_vDimsRaw.pallets) ? _vDimsRaw.pallets : null);
        }
        var _vDimsLogged = Array.isArray(_vDimsPallets) && _vDimsPallets.length > 0;
        var _vDimsSummary = (_vDimsRaw && _vDimsRaw.summary) || '';
        if (_vDimsLogged) {
          var _vDisplay = _vDimsSummary || (_vDimsPallets.length + ' pallet' + (_vDimsPallets.length === 1 ? '' : 's'));
          _dimsLineHtml += '<span class="rqd-vendor-dims__inline" title="' + esc(_vDimsSummary || _vDisplay) + '">📦 ' + esc(_vDisplay) + '</span>';
          _dimsLineHtml += '<button type="button" class="rqd-vendor-dims__link" data-fb-action="edit-vendor-dims" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '">Edit</button>';
        } else {
          _dimsLineHtml += '<button type="button" class="rqd-vendor-dims__link" data-fb-action="add-vendor-dims" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" title="Log this vendor\'s pallet weight + dims (used when WE arrange shipping via our broker)">Log dims</button>';
        }
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      // Tracking fields inline — always shown so reps can fill in carrier
      // / type / number directly without needing to check the box first.
      // The box auto-checks once all three are filled (see doTrackingSave).
      // --fulfilled modifier: after Shopify fulfillment fires, the inputs
      // green-tint to mirror the .rqd-toggle__date-input--filled "completed"
      // styling. Lock state is enforced via the disabled attribute on each
      // input/button below; this class is purely visual.
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Wrap the
         tracking inputs + sync button in row-controls so they wrap
         as a coherent right-aligned cluster (see bucket-mode block
         above + .rqd-vendor-row-controls CSS rule). */
      html += '<div class="rqd-vendor-row-controls">';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (Ryan 2026-09-26) — `--filled`: a row carrying a number ghosts like
         any filled DD cell, told or not ("when we fill them out and choose not to send the email, that
         shouldn't disqualify the fields"). LOOK ONLY — no lock; `--fulfilled` still owns the lock. */
      html += '<div class="rqd-vendor-tracking' + (poSent ? '' : ' rqd-vendor-tracking--locked') + (isCompleted ? ' rqd-vendor-tracking--fulfilled' : '') + (String(ord.tracking || '').trim() ? ' rqd-vendor-tracking--filled' : '') + '" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '"' + (isOnlineOrder ? ' data-online="1"' : '') + (isCompleted || !poSent ? ' title="' + esc(lockTitle) + '"' : '') + '>';
      // Status indicator (Saving... / Saved ✓) — sits at the START of
      // the row with a reserved min-width so the carrier dropdown +
      // tracking inputs + Sync button don't shift L/R when the
      // indicator's text appears or disappears.
      html += '<span class="rqd-vendor-tracking__status"></span>';
      // Red × button — visible only once any tracking field has content.
      // Clears the row + unchecks the box. Same pattern as the AR/AP
      // payment-row remove button.
      // Hide the clear-× button once Shopify has the fulfillment — clearing
      // would orphan the shipping data while the order stays fulfilled in
      // Shopify. To undo, the rep cancels the fulfillment in Shopify Admin.
      var hasAnyTrackingData = !!(ord.carrier || ord.tracking || ord.trackingType);
      /* CUSTOM_OFFLINECLEAR_BY_CLAUDE (AIOPS `2e3e604e835c`, Ryan 2026-09-24) — an OFFLINE row's number is on
         the record the moment it is typed or stamped, so its × is marked persisted too and goes through POST
         /deal-tracking-clear. And it stays VISIBLE on a notified row, because that is the one row a wrong
         number cannot otherwise leave (an online row has its Undo; an offline one had nothing). */
      var _removeVisible = hasAnyTrackingData && (!isCompleted || (!isOnlineOrder && !!ord.tracking));
      var _removePersisted = !!ord.tracking;
      /* CUSTOM_TRACKINGCLEAR_BY_CLAUDE (AIOPS `5982e62242c1`, Ryan 2026-09-22 - SHOP-2026) — ⛔ AN
         ONLINE ROW'S NUMBER IS ON THE RECORD NOW. The × handler (dashboard-main.js) was written when
         an online row's inputs lived only in the DOM until Mark Fulfilled; the agent stamp rail and
         the ship-notice sweep have persisted `ordering[vendor].tracking` on online deals since
         September, and SHOP-2026 sat with another order's FedEx number because the × only emptied
         the boxes on screen. `data-persisted` tells the handler there is a record to clear, so it
         calls POST /deal-tracking-clear instead of returning early. */
      html += '<button type="button" class="rqd-vendor-tracking__remove' + (_removeVisible ? ' rqd-vendor-tracking__remove--visible' : '') + '" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '"' + (_removePersisted ? ' data-persisted="1" data-tracking="' + esc(ord.tracking) + '" data-carrier="' + esc(ord.carrier || '') + '"' : '') + ' title="Clear shipment" aria-label="Clear shipment">&times;</button>';
      // Online orders → custom autocomplete dropdown (<input> + filtered
      // option menu) against ONLINE_CARRIERS. Custom rather than browser
      // <datalist> because datalist's popup can't be height-constrained
      // — a 50-entry list overflows the viewport. The wrapper element
      // provides positioning context for the absolutely-positioned menu;
      // the input keeps its existing class so all the styling and
      // tracking-row layout still apply unchanged. Free-typing falls
      // through as the carrier value (worker normalizes / requires URL
      // for non-Shopify-known names).
      // Single autocomplete input for both online + offline. Online
      // path was the new pattern we built when carrier autocomplete
      // first landed; offline used to have a <select>+Other-input
      // split. Backporting the autocomplete brings both paths to
      // the same UX: type to search, "Other — Use 'X'" sentinel
      // commits free-typed text as the carrier value.
      var carrierVal = ord.carrier || '';
      html += '<div class="rqd-carrier-ac">';
      html += '<input class="rqd-vendor-tracking__carrier" type="text" placeholder="Carrier..." autocomplete="off" maxlength="60" value="' + esc(carrierVal) + '"' + lockAttrs + '>';
      html += '<div class="rqd-carrier-ac__menu" role="listbox"></div>';
      html += '</div>';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19, v2) — Tracking
         type dropdown restored AND extended to online rows.
         Originally offline-only because online customer emails
         went through Shopify (no admin control over phrasing).
         Now that Abby Rivers (AI) authors the customer email, the
         type label feeds her natural phrasing — e.g. "tracked via
         FedEx Freight Priority using PRO Number: 7391218063"
         instead of the generic colon form. Optional — leaving it
         blank still works (Abby falls back to generic "Tracking
         number" phrasing). */
      var typeIsOther = ord.trackingType === 'Other' || (ord.trackingType && trackingTypes.indexOf(ord.trackingType) === -1);
      var typeVal = typeIsOther ? 'Other' : (ord.trackingType || '');
      html += '<select class="rqd-vendor-tracking__type"' + lockAttrs + '>';
      html += '<option value=""' + (!typeVal ? ' selected' : '') + '>-- Type --</option>';
      for (var ti = 0; ti < trackingTypes.length; ti++) {
        html += '<option value="' + trackingTypes[ti] + '"' + (typeVal === trackingTypes[ti] ? ' selected' : '') + '>' + trackingTypes[ti] + '</option>';
      }
      html += '</select>';
      html += '<input class="rqd-vendor-tracking__type-other" placeholder="Type label" maxlength="20" value="' + esc(typeIsOther && ord.trackingType !== 'Other' ? ord.trackingType : (ord.trackingTypeOther || '')) + '"' + (typeIsOther ? '' : ' style="display:none"') + lockAttrs + '>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      html += '<input class="rqd-vendor-tracking__input" placeholder="Number" maxlength="30" value="' + esc(ord.tracking || '') + '"' + lockAttrs + '>';
      // Tracking URL is shown ONLY when the carrier is a custom name
      // (not on ONLINE_CARRIERS) — Shopify auto-builds the customer's
      // tracking link for known carriers from the tracking #, so the
      // URL field would just be noise there. Toggled on the fly by the
      // change listener whenever the carrier value flips between known
      // and custom.
      //
      // "Send to Customer" / "Synced ✓" lives in its own .rqd-invoice
      // slot (sibling of .rqd-vendor-tracking) so it right-aligns in
      // the same column as Generate PO / Generate Invoice / Request
      // Freight on the other cards.
      // CUSTOM_RFQPORTAL_BY_CLAUDE — also render URL input on offline
      // rows with a linked Shopify order; the Shopify push path needs
      // it for custom carriers same as online rows do.
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — URL field is
         now ALWAYS visible (was conditional on custom carrier).
         When provided, overrides Shopify's auto-built URL.
         Ryan 2026-05-27: render on OFFLINE rows too (not just online /
         linked-online) so reps can attach a tracking website to offline
         freight shipments — and the carrier→URL memory (recall/remember in
         dashboard-main.js) now auto-fills it on repeat carriers. */
      html += '<input class="rqd-vendor-tracking__url" placeholder="Tracking URL — optional, auto-linked for known carriers" title="Leave blank for any carrier we already know (FedEx, UPS, Daylight, Ward, ABF, Estes, SAIA, ODFL, R+L, XPO, TForce...) — the customer email builds the deep link from the tracking/PRO number. Fill this in only to OVERRIDE that, or for a carrier we have no link for yet." maxlength="500" value="' + esc(trackingUrl) + '"' + lockAttrs + '>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      // END CUSTOM_RFQPORTAL_BY_CLAUDE
      html += '</div>';  // close .rqd-vendor-tracking
      if (isOnlineOrder) {
        // Wrapper picks up the parent flex row's 12px gap on its own;
        // dropped the .rqd-invoice class because it carries
        // `margin-left:auto + padding-right:12px` (right-pin used by
        // AR/AP buttons), which produced an inconsistent gap before
        // the Send-to-Customer button.
        html += '<div class="rqd-vendor-shopify-sync">';
        if (shopifyFulfillmentId) {
          // Use the same <button> + .rqd-invoice__link classes as the
          // pre-fulfillment Mark Fulfilled button so the shape (28px ×
          // 140px pill) doesn't change after success — only the color
          // shifts to green. --fulfilled modifier carries the green
          // tint matching the AR/AP filled-date "completed" treatment.
          // (Undo lives inline after the fulfilled date — see the namegroup above.)
          /* CUSTOM_PICKUPDAY_BY_CLAUDE -- an online LTL row reads Fulfilled in Shopify the moment the
             PRO is stamped, so this caption is the only place the pickup-day / shipped-notice rule shows. */
          var _pdCapOn = (!(_cse && _cse.at) && !(ord && ord.customerNotifiedAt)) ? pickupDayCaption(_cse, ord) : null;
          if (_pdCapOn) html += '<div class="rqd-vendor-sched-wrap">';
          html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link rqd-vendor-tracking__sync--fulfilled" disabled title="Fulfilled in Shopify">Fulfilled ✓</button>';
          if (_pdCapOn) html += '<span class="rqd-vendor-notify__done" title="' + esc(_pdCapOn.title) + '">' + esc(_pdCapOn.text) + '</span></div>';
        } else if (!poSent) {
          html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link rqd-invoice__link--disabled" disabled title="' + esc(lockTitle) + '">Mark as Fulfilled</button>';
        } else {
          html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" data-order-name="' + esc(q.reference) + '" style="cursor:pointer;">Mark as Fulfilled</button>';
        }
        html += '</div>';
      } else {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Offline deal (pure OR offline-paid-online).
           Per Ryan 2026-05-27: an offline deal that the customer happened to pay
           via the Shopify hosted link is STILL an offline order in every sense —
           the rep + Abby own the customer email. So ALL offline rows share this
           one "Mark Fulfilled" flow (Abby email / no-tracking mark). When the
           draft was paid online (q.linkedOnlineOrderName set), the worker ALSO
           silently marks that real Shopify order fulfilled (notifyCustomer:false,
           no Shopify email) — handled server-side via the orderName the handler
           passes. The button is always
           rendered (like online's Mark-as-Fulfilled) so it's present right
           after the tracking auto-save; the click handler checks tracking is
           entered, and it's disabled when no customer email is on file. Once
           sent it flips to a disabled
           "Fulfilled ✓" (matching online) and the tracking inputs ghost +
           lock. Mirrors the online Mark-as-Fulfilled slot, no Shopify side-
           effect. */
        var _notifiedAt = _cse.at || (ord && ord.customerNotifiedAt) || '';
        // Scheduled = business-hours-deferred, queued but not yet sent.
        var _scheduledFor = (!_notifiedAt && _cse.scheduledFor) ? _cse.scheduledFor : '';
        // Always render the notify slot (like online's always-present
        // Mark-as-Fulfilled) so it survives the in-place tracking auto-save
        // WITHOUT needing a detail-panel re-render; the click handler
        // validates that tracking is actually entered before opening the modal.
        html += '<div class="rqd-vendor-shopify-sync">';
        if (_notifiedAt || offlineNoTrack) {
          if (offlineNoTrack && !_notifiedAt) {
            // Fulfilled without tracking → same green "Fulfilled ✓" pill a
            // normal order shows, with a small Undo stacked beneath it (no
            // email was sent; the Undo re-opens the tracking inputs).
            html += '<div class="rqd-vendor-notrack-wrap">';
            html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link rqd-vendor-tracking__sync--fulfilled" disabled title="Fulfilled — no tracking (no email sent)">Fulfilled ✓</button>';
            html += '<button type="button" class="rqd-vendor-notrack-undo rqd-vendor-notify__undo" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" title="Undo — re-open the tracking fields">Undo</button>';
            html += '</div>';
          } else {
            // Completed → disabled "Fulfilled ✓", matching online's post-
            // fulfillment button (no Resend, no separate "Notified" text).
            html += '<button type="button" class="rqd-vendor-tracking__sync rqd-invoice__link rqd-vendor-tracking__sync--fulfilled" disabled title="Customer emailed ' + esc(formatDate(_notifiedAt)) + '">Fulfilled ✓</button>';
          }
        } else if (_scheduledFor) {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-04) — "Scheduled" caption moved
             BELOW the Send-now button (was to its LEFT, which widened the slot and
             threw off the buttons' right-edge alignment). Absolutely positioned in
             the wrap so it adds no height — the button keeps the same position +
             vertical alignment as the Mark Fulfilled / other rows' buttons. */
          html += '<div class="rqd-vendor-sched-wrap">';
          html += '<button type="button" class="rqd-vendor-notify rqd-invoice__link" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" style="cursor:pointer;" title="Send now or reschedule">Send now</button>';
          /* CUSTOM_PICKUPHOLD_BY_CLAUDE (AIOPS `5badf69b3563`, Ryan 2026-09-18) — ⌛ ON THE PILL.
             Ryan: *"show the same ⌛ scheduled cue used elsewhere … so reps/agents can see it's
             queued, not missing and not already sent."* The same glyph the Activity card puts on a
             held email, which is what this is.

             ⛔ THE HOLD REASON IS PART OF THE PILL, not only of the tooltip. "Scheduled" beside a
             tracking number an hour after the PRO was stamped reads as a delay nobody chose; "held
             until the day after pickup" is the rule doing its job, and a touch user never sees a
             title attribute. The pill only ever renders now because `scheduledFor` is written to
             the RECORD at queue time — before this it was set client-side by the pop-up and lost
             on the next refresh, and an agent-queued notice never had it at all. */
          var _heldForPickup = (_cse && _cse.heldForPickup) || null;
          var _pdCap = pickupDayCaption(_cse, ord); // CUSTOM_PICKUPDAY_BY_CLAUDE
          if (_pdCap) {
            html += '<span class="rqd-vendor-notify__done" title="' + esc(_pdCap.title) + '">' + esc(_pdCap.text) + '</span>';
          } else {
          html += '<span class="rqd-vendor-notify__done" title="'
            + esc(_heldForPickup && _heldForPickup.why
              ? _heldForPickup.why
              : ('Abby emails the customer at the next business-hours window (' + formatDate(_scheduledFor) + ')'))
            + '">⌛ ' + (_heldForPickup ? 'Holds till ' + esc(formatDate(_heldForPickup.releaseOn)) : 'Scheduled') + '</span>';
          }
          html += '</div>';
        } else if (vendorEarlyStampVeto(q, ord, vKey)) {
          /* CUSTOM_EARLYSTAMP_BY_CLAUDE (AIOPS fb2ebf0cafb8, Ryan 2026-09-21) — the early stamp,
             named. The PRO is on the row, the pickup is booked and unconfirmed, and the customer
             has not been told: the step above deliberately shows no ✓ (see vendorIsShipped in
             dashboard-main.js), so the row says why instead of looking half-filled. Same wrap as
             the Scheduled pill so the button keeps its slot. Mark Fulfilled still works - the
             notify route parks the customer notice past the pickup floor by itself. */
          html += '<div class="rqd-vendor-sched-wrap">';
          html += '<button type="button" class="rqd-vendor-notify rqd-invoice__link" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" style="cursor:pointer;" title="Mark this shipment fulfilled. The customer notice holds until the day after the booked pickup.">Mark Fulfilled</button>';
          html += '<span class="rqd-vendor-notify__done" title="Pickup booked for ' + esc(formatDate(ord.pickupScheduledFor)) + ' and not yet confirmed. The customer is not told the order shipped on the day the truck is only due.">⌛ Stamped. Customer not told yet.</span>';
          html += '</div>';
        } else if (ord.deliveredAt) {
          /* CUSTOM_DDCARDCOUNT_BY_CLAUDE (Ryan 2026-09-26 - RW-1317-C PowerBlock) — DELIVERED, NEVER TOLD:
             NO EMAIL IS OWED, SO THE BUTTON STOPS ASKING FOR ONE. The number reached us after FedEx had
             delivered, and Ryan's rule is that tracking which arrives after delivery is never emailed
             (memory no-shipping-email-after-delivery). A lit "Mark Fulfilled" read as a task left undone.
             ⚠️ GREYED, NOT DISABLED (his words: "still allow it to be clickable"): the same handler, the
             same email, for the rare case somebody still wants the customer to have the number.
             */
          var _dlvDay = String(ord.deliveredAtRaw || ord.deliveredAt || '').slice(0, 10); // the carrier's LOCAL date when it gave one
          /* ⛔ NO CAPTION UNDER IT (Ryan, same night): "Delivered 9/19/2026 · not emailed" in the Scheduled
             pill's slot was bold, ran off the card, and read as nonsense. The greyed button is the whole
             signal; the why lives in its title. Same slot as the plain button, so the row does not shift. */
          html += '<button type="button" class="rqd-vendor-notify rqd-invoice__link rqd-vendor-notify--quiet" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" style="cursor:pointer;" title="Delivered ' + esc(formatDate(_dlvDay)) + ' before we had the tracking number, so no shipping email is owed. Click only if you still want to email the customer.">Mark Fulfilled</button>';
          /* END CUSTOM_DDCARDCOUNT_BY_CLAUDE */
        } else {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — single "Mark
             Fulfilled" button. The click handler routes by tracking: a number
             entered → notify the customer (Abby email, CCs the rep); none
             entered → mark fulfilled with no tracking + no email (customer
             pickup / tracking unavailable). */
          html += '<button type="button" class="rqd-vendor-notify rqd-invoice__link" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '" style="cursor:pointer;" title="Mark this shipment fulfilled. With a tracking number it emails the customer (CCs the rep); with none it marks fulfilled without an email.">Mark Fulfilled</button>';
        }
        html += '</div>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      }
      html += '</div>';  // close .rqd-vendor-row-controls (Ryan 2026-05-22)
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — dims trigger on its own
         full-width line BELOW the name (indented to align under it), so the
         tracking controls get line 1 to themselves next to the name. */
      if (_dimsLineHtml) html += '<div class="rqd-vendor-dims-line">' + _dimsLineHtml + '</div>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      html += laterShipmentsLineHtml(q, vKey, ord, { canAdd: isOnlineOrder && !!ord.tracking && poSent }); // CUSTOM_MULTISHIP_BY_CLAUDE (AIOPS 17a342129a10)
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Empty form-
         expansion container. Sits in the wrapped flex row beneath
         the tracking inputs; populated by handleVendorDimsOpen when
         admin clicks the inline Log dims / Edit link above. Stays
         empty (0 height) when idle. */
      if (!isOnlineOrder) {
        html += '<div class="rqd-vendor-dims-block" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '"></div>';
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      html += '</div>';  // close .rqd-vendor-item
    });
    html += '</div>';
    return html;
  }

  /* CUSTOM_MULTISHIP_BY_CLAUDE (AIOPS `17a342129a10`, Ryan 2026-09-23/26 - SHOP-1999) — LATER SHIPMENTS
     FROM THE SAME VENDOR. Perform Better shipped SHOP-1999 twice (Sep 1, then the backordered Wall Mat
     Rack); the row had room for one number, so the second could only land by clearing the first. The
     first shipment keeps the row's own inputs; each later one (`ordering[vendor].additionalShipments`,
     written by POST /deal-tracking-stamp {additional:true}) is a line underneath: its number linked,
     who added it, and whether the customer has been emailed about it. "+ Add shipment" opens a small
     form that posts the same route (dashboard-main.js handleLaterShipmentAction). Online rows only,
     and only once the row carries a first number. Pure: returns '' when there is nothing to say. */
  var LATER_ORDINALS = ['Second', 'Third', 'Fourth', 'Fifth'];
  function ymdShort(ymd) {
    var m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? Number(m[2]) + '/' + Number(m[3]) : '';
  }
  function laterShipmentsLineHtml(q, vKey, ord, opts) {
    ord = ord || {};
    var extra = Array.isArray(ord.additionalShipments) ? ord.additionalShipments : [];
    var canAdd = !!(opts && opts.canAdd);
    if (!extra.length && !canAdd) return '';
    var parts = [];
    if (extra.length && ord.firstDeliveredOn) {
      parts.push('<span class="rqd-vendor-ships__item">First shipment delivered ' + esc(ymdShort(ord.firstDeliveredOn)) + '</span>');
    }
    extra.forEach(function (e, i) {
      if (!e || !e.tracking) return;
      var num = e.trackingUrl
        ? '<a href="' + esc(e.trackingUrl) + '" target="_blank" rel="noopener">' + esc(e.tracking) + '</a>'
        : esc(e.tracking);
      var told = e.customerNotifiedAt ? 'customer emailed ' + esc(formatDate(e.customerNotifiedAt)) : 'customer not emailed yet';
      parts.push('<span class="rqd-vendor-ships__item"><b>' + (LATER_ORDINALS[i] || 'Later') + ' shipment:</b> '
        + esc(e.carrier || ord.carrier || '') + ' ' + num
        + (e.recordedAt ? ' &middot; added ' + esc(formatDate(e.recordedAt)) + (e.recordedBy ? ' by ' + esc(e.recordedBy) : '') : '')
        + ' &middot; ' + told + '</span>');
    });
    if (canAdd && extra.length < LATER_ORDINALS.length) {
      var dr = ' data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '"';
      parts.push('<span class="rqd-vendor-ships__item">'
        + '<button type="button" class="rqd-vendor-dims__link" data-ship-action="add-shipment"' + dr + ' title="A later shipment from this vendor (a partial or a backorder). The first number stays.">+ Add shipment</button>'
        + '<span class="rqd-vendor-ships__form" style="display:none;">'
        + '<input class="rqd-vendor-ships__carrier" type="text" placeholder="Carrier" maxlength="40" value="' + esc(ord.carrier || '') + '">'
        + '<input class="rqd-vendor-ships__number" type="text" placeholder="Tracking number" maxlength="40">'
        + '<button type="button" class="rqd-vendor-dims__link" data-ship-action="save-shipment"' + dr + '>Save</button>'
        + '<button type="button" class="rqd-vendor-dims__link" data-ship-action="cancel-shipment"' + dr + '>Cancel</button>'
        + '</span></span>');
    }
    return '<div class="rqd-vendor-dims-line rqd-vendor-ships" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(vKey) + '">' + parts.join('') + '</div>';
  }
  /* END CUSTOM_MULTISHIP_BY_CLAUDE */

  function initShippingTrackingCard(deps) {
    deps = deps || {};
    esc = deps.esc;
    formatDate = deps.formatDate;
    money = deps.money;
    vendorNamesMatch = deps.vendorNamesMatch;
    isPostShipInvoiceVendor = deps.isPostShipInvoiceVendor;
    vendorIsShipped = deps.vendorIsShipped;
    vendorEarlyStampVeto = deps.vendorEarlyStampVeto || function () { return false; }; // CUSTOM_EARLYSTAMP_BY_CLAUDE (AIOPS fb2ebf0cafb8)
    sectionTitleHtml = deps.sectionTitleHtml;
    carriers = deps.carriers || [];
  }

  window.RQD.buildShippingTrackingCard = buildShippingTrackingCard;
  window.RQD.renderCustomerWindowRow = customerWindowRowHtml; // CUSTOM_WINDOWGHOST_BY_CLAUDE - the save redraws every window row
  window.RQD.pickupDayCaption = pickupDayCaption; // CUSTOM_PICKUPDAY_BY_CLAUDE - lifted by the test
  window.RQD.laterShipmentsLineHtml = laterShipmentsLineHtml; // CUSTOM_MULTISHIP_BY_CLAUDE - lifted by the test
  window.RQD.initShippingTrackingCard = initShippingTrackingCard;
})();
